import React from 'react';
import { TFunction } from 'i18next';
import * as Yup from 'yup';
import _get from 'lodash/get';
import { WidgetTemplatesKindEnum } from 'graphql-common';
import {
  FieldChangeCallBackArgs,
  FieldItem,
  Values,
} from '@lib/interfaces/form';
import Input from '@lib/components/Input/Input';
import { InputTypes } from '@lib/components/Input/enums';
import CheckBoxSingleBool from '@lib/components/CheckBoxSingleBool/CheckBoxSingleBool';
import CheckBoxAsToggleCard from '@lib/components/CheckBoxAsToggleCard';
import Typography from '@lib/components/Typography/Typography';
import {
  SupportiveMessageColor,
  SupportiveMessageIconColor,
  SupportiveMessageType,
} from '@lib/components/SupportiveMessage/SupportiveMessage';
import RadioButtonGroup, {
  RadioButtonWrapViews,
} from '@lib/components/RadioButtonGroup/RadioButtonGroup';
import RadioButtonAsChipColor from '@lib/components/RadioButtonAsChipColor/RadioButtonAsChipColor';
import RadioButtonAsButton from '@lib/components/RadioButtonAsButton/RadioButtonAsButton';
import getColorOptions from '@lib/components/Chip/utils/colorEnum';
import TextEditor from '@lib/components/TextEditor/TextEditor';
import ImageUploader, {
  ImageUploaderMode,
} from '@lib/components/ImageUploader/ImageUploader';
import {
  FileSizeLimits,
  fileSizeLimit,
  UppyImageAllowedFileTypes,
} from '@lib/enums/mimeTypes';
import {
  MAX_TEXT_EDITOR_FIELD_LENGTH,
  MAX_TEXT_FIELD_LENGTH,
} from '@lib/enums/form';
import { getOptionValidation } from '@lib/utils/validationUtils';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import WidgetTemplateFieldNames from '@lib/enums/fieldNames/widgetTemplateFieldNames';
import formStyles from '@lib/assets/styles/Form.module.scss';
import styles from './getWidgetTemplateSharedFields.module.scss';

// Available widget kinds for creation (only Announcement and ExternalLink)
export const CREATABLE_WIDGET_KINDS = [
  WidgetTemplatesKindEnum.Announcement,
  WidgetTemplatesKindEnum.ExternalLink,
];

/**
 * Gets widget kind options for RadioButtonGroup
 */
export function getWidgetKindOptions(t: TFunction) {
  return [
    {
      label: t('announcement'),
      value: WidgetTemplatesKindEnum.Announcement,
      leftIcon: 'campaign',
    },
    {
      label: t('external-link'),
      value: WidgetTemplatesKindEnum.ExternalLink,
      leftIcon: 'link',
    },
  ];
}

/**
 * Gets widget kind description based on selected kind
 */
function getWidgetKindDescription(
  kind: WidgetTemplatesKindEnum | undefined,
  t: TFunction,
): string | undefined {
  const descriptions: Partial<Record<WidgetTemplatesKindEnum, string>> = {
    [WidgetTemplatesKindEnum.Announcement]: t('home.announcement-description'),
    [WidgetTemplatesKindEnum.ExternalLink]: t('home.external-link-description'),
  };
  return kind ? descriptions[kind] : undefined;
}

/**
 * Check if current kind is Announcement (for dynamic visibility)
 */
export function isAnnouncement(values: Values): boolean {
  return (
    _get(values, ['kind', 'value']) === WidgetTemplatesKindEnum.Announcement
  );
}

/**
 * Check if current kind is ExternalLink (for dynamic visibility)
 */
export function isExternalLink(values: Values): boolean {
  return (
    _get(values, ['kind', 'value']) === WidgetTemplatesKindEnum.ExternalLink
  );
}

/**
 * Check if kind is one of the creatable types (for dynamic visibility)
 */
export function isCreatableKind(values: Values): boolean {
  return isAnnouncement(values) || isExternalLink(values);
}

interface SharedFieldsConfig {
  /** Use dynamic visibility (for create form) vs static kind (for edit form) */
  useDynamicVisibility: boolean;
  /** Fixed widget kind (for edit form) */
  widgetKind?: WidgetTemplatesKindEnum;
}

interface WidgetKindSelectorConfig {
  /** Single kind mode - shows only one option (for edit form) */
  singleKind?: WidgetTemplatesKindEnum;
  /** Whether to show preview button */
  showPreview?: boolean;
  /** Preview button click handler */
  onPreviewClick?: () => void;
  /** Callback triggered when form values change */
  onFieldChange?: (values: Values) => void;
}

/**
 * Returns the widget kind selector field
 * For create form: shows all options
 * For edit form: shows single option (read-only) when singleKind is provided
 */
export function getWidgetKindSelectorField(
  t: TFunction,
  config?: WidgetKindSelectorConfig,
): FieldItem {
  const { singleKind, onFieldChange } = config || {};

  // For edit form with single kind, show only that option
  const options = singleKind
    ? getWidgetKindOptions(t).filter((opt) => opt.value === singleKind)
    : getWidgetKindOptions(t);

  return {
    name: WidgetTemplateFieldNames.Kind,
    element: RadioButtonGroup,
    validation: getOptionValidation({ required: true, t }),
    componentProps: {
      options,
      RadioButtonComponent: RadioButtonAsButton,
      wrapView: RadioButtonWrapViews.row,
      // Disable interaction in single-kind mode (edit form)
      ...(singleKind && { isDisabled: true }),
    },
    message: {
      getTextFn: (values: Values) => {
        const kindValue = _get(values, ['kind', 'value']) as
          WidgetTemplatesKindEnum | undefined;
        return getWidgetKindDescription(kindValue, t);
      },
      type: SupportiveMessageType.Transparent,
      color: SupportiveMessageColor.Gray,
      iconColor: SupportiveMessageIconColor.Inner,
      leftIcon: 'info',
    },
    formItemClassName: formStyles.formColumn,
    isUseWatch: true,
    fieldChangeCallBack: ({ values }: FieldChangeCallBackArgs) => {
      // Trigger callback on field change to keep preview in sync
      if (onFieldChange) {
        onFieldChange(values);
      }
    },
  };
}

/**
 * Returns a preview button field
 * Can be used in both create and edit forms
 */
export function getPreviewButtonField(
  t: TFunction,
  config?: WidgetKindSelectorConfig,
): FieldItem {
  const { onPreviewClick, singleKind } = config || {};

  return {
    name: WidgetTemplateFieldNames.PreviewButton,
    customRender: (values: Values) => {
      // Determine if button should be disabled based on widget kind
      let isDisabled = false;
      const kindValue = _get(values, ['kind', 'value']) as
        WidgetTemplatesKindEnum | undefined;

      if (kindValue === WidgetTemplatesKindEnum.Announcement) {
        // For Announcement: disable if Content field is empty
        const content = _get(
          values,
          WidgetTemplateFieldNames.Content,
          '',
        ) as string;
        isDisabled = !content || content.trim() === '';
      } else if (kindValue === WidgetTemplatesKindEnum.ExternalLink) {
        // For ExternalLink: disable if Link field is empty
        const link = _get(values, WidgetTemplateFieldNames.Link, '') as string;
        isDisabled = !link || link.trim() === '';
      }

      return (
        <div className={styles.previewButtonWrapper}>
          <Button
            buttonText={t('preview')}
            buttonType={ButtonTypes.primaryOutlined}
            leftIcon="visibility"
            onClick={onPreviewClick}
            disabled={isDisabled}
          />
        </div>
      );
    },
    formItemClassName: formStyles.formColumn,
    // For create form, only show when a kind is selected
    ...(singleKind ? {} : { isVisibleFn: isCreatableKind }),
    isUseWatch: true,
  };
}

/**
 * Returns fields for Announcement widget template
 */
export function getAnnouncementFields(
  t: TFunction,
  config: SharedFieldsConfig,
): FieldItem[] {
  const { useDynamicVisibility, widgetKind } = config;
  const shouldShow = useDynamicVisibility
    ? isAnnouncement
    : () => widgetKind === WidgetTemplatesKindEnum.Announcement;

  // For edit form, don't render if kind doesn't match
  if (
    !useDynamicVisibility &&
    widgetKind !== WidgetTemplatesKindEnum.Announcement
  ) {
    return [];
  }

  const fields: FieldItem[] = [
    {
      name: WidgetTemplateFieldNames.AnnouncementSettingsTitle,
      customRender: () => (
        <Typography variant="h3">{t('home.template-settings')}</Typography>
      ),
      ...(useDynamicVisibility && { isVisibleFn: shouldShow }),
    },
    {
      name: WidgetTemplateFieldNames.BackgroundColor,
      label: t('announcement-box-color'),
      element: RadioButtonGroup,
      validation: useDynamicVisibility
        ? Yup.object()
            .shape({
              value: Yup.string(),
              label: Yup.string(),
            })
            .when(WidgetTemplateFieldNames.Kind, {
              is: (kind: { value: string }) =>
                kind?.value === WidgetTemplatesKindEnum.Announcement,
              then: (schema) => schema.required(t('required-field-error')),
              otherwise: (schema) => schema.nullable(),
            })
        : Yup.object()
            .shape({
              value: Yup.string(),
              label: Yup.string(),
            })
            .required(t('required-field-error')),
      componentProps: {
        options: getColorOptions({ t }),
        RadioButtonComponent: RadioButtonAsChipColor,
        wrapView: RadioButtonWrapViews.colors,
      },
      isOptionalFn: () => false,
      ...(useDynamicVisibility && { isVisibleFn: shouldShow }),
    },
    {
      name: WidgetTemplateFieldNames.Content,
      label: t('message-content'),
      element: TextEditor,
      validation: useDynamicVisibility
        ? Yup.string()
            .max(MAX_TEXT_EDITOR_FIELD_LENGTH, t('max-text-length-field-error'))
            .when(WidgetTemplateFieldNames.Kind, {
              is: (kind: { value: string }) =>
                kind?.value === WidgetTemplatesKindEnum.Announcement,
              then: (schema) => schema.required(t('required-field-error')),
              otherwise: (schema) => schema.nullable(),
            })
        : Yup.string()
            .max(MAX_TEXT_EDITOR_FIELD_LENGTH, t('max-text-length-field-error'))
            .required(t('required-field-error')),
      isOptionalFn: () => false,
      ...(useDynamicVisibility && { isVisibleFn: shouldShow }),
    },
    {
      name: WidgetTemplateFieldNames.Image,
      element: ImageUploader,
      componentProps: {
        label: t('image'),
        mode: ImageUploaderMode.Compact,
        showOfflineMessage: true,
        allowedFileTypes: UppyImageAllowedFileTypes,
        note: t('image-upload-validation-msg', {
          size: FileSizeLimits.Image,
        }),
        maxFileSize: fileSizeLimit,
        uploadButtonProps: {
          buttonText: t('upload-image'),
          leftIcon: 'upload',
          fullWidth: true,
        },
        useReuploadButton: true,
        showDeleteAllButton: true,
      },
      ...(useDynamicVisibility && { isVisibleFn: shouldShow }),
    },
  ];

  return fields;
}

/**
 * Returns fields for External Link widget template
 */
export function getExternalLinkFields(
  t: TFunction,
  config: SharedFieldsConfig,
): FieldItem[] {
  const { useDynamicVisibility, widgetKind } = config;
  const shouldShow = useDynamicVisibility
    ? isExternalLink
    : () => widgetKind === WidgetTemplatesKindEnum.ExternalLink;

  // For edit form, don't render if kind doesn't match
  if (
    !useDynamicVisibility &&
    widgetKind !== WidgetTemplatesKindEnum.ExternalLink
  ) {
    return [];
  }

  const fields: FieldItem[] = [
    {
      name: WidgetTemplateFieldNames.ExternalLinkSettingsTitle,
      customRender: () => (
        <Typography variant="h3">{t('home.template-settings')}</Typography>
      ),
      ...(useDynamicVisibility && { isVisibleFn: shouldShow }),
    },
    {
      name: WidgetTemplateFieldNames.Link,
      label: t('home.link-destination'),
      element: Input,
      validation: useDynamicVisibility
        ? Yup.string()
            .url(t('invalid-url-error'))
            .when(WidgetTemplateFieldNames.Kind, {
              is: (kind: { value: string }) =>
                kind?.value === WidgetTemplatesKindEnum.ExternalLink,
              then: (schema) => schema.required(t('required-field-error')),
              otherwise: (schema) => schema.nullable(),
            })
        : Yup.string()
            .url(t('invalid-url-error'))
            .required(t('required-field-error')),
      isOptionalFn: () => false,
      ...(useDynamicVisibility && { isVisibleFn: shouldShow }),
    },
  ];

  return fields;
}

/**
 * Returns widget details fields (title, subtitle, icon)
 */
export function getWidgetDetailsFields(
  t: TFunction,
  config: SharedFieldsConfig,
): FieldItem[] {
  const { useDynamicVisibility } = config;

  const fields: FieldItem[] = [
    {
      name: WidgetTemplateFieldNames.WidgetDetailsTitle,
      customRender: () => (
        <Typography variant="h3">{t('home.widget-details')}</Typography>
      ),
      ...(useDynamicVisibility && { isVisibleFn: isCreatableKind }),
    },
    {
      name: WidgetTemplateFieldNames.Title,
      label: t('home.widget-title'),
      element: Input,
      validation: Yup.string()
        .max(MAX_TEXT_FIELD_LENGTH, t('max-text-length-field-error'))
        .required(t('required-field-error')),
      isOptionalFn: () => false,
      ...(useDynamicVisibility && { isVisibleFn: isCreatableKind }),
    },
    {
      name: WidgetTemplateFieldNames.Description,
      label: t('home.subtitle'),
      element: Input,
      componentProps: {
        type: InputTypes.textarea,
      },
      validation: Yup.string().max(
        MAX_TEXT_FIELD_LENGTH,
        t('max-text-length-field-error'),
      ),
      ...(useDynamicVisibility && { isVisibleFn: isCreatableKind }),
    },
    {
      name: WidgetTemplateFieldNames.IconImage,
      element: ImageUploader,
      componentProps: {
        label: `${t('home.icon-image')} (${t('optional')})`,
        mode: ImageUploaderMode.Compact,
        showOfflineMessage: true,
        allowedFileTypes: UppyImageAllowedFileTypes,
        note: t('image-upload-validation-msg', {
          size: FileSizeLimits.Image,
        }),
        maxFileSize: fileSizeLimit,
        uploadButtonProps: {
          buttonText: t('upload-image'),
          leftIcon: 'upload',
          fullWidth: true,
        },
        useReuploadButton: true,
        showDeleteAllButton: true,
      },
      ...(useDynamicVisibility && { isVisibleFn: isCreatableKind }),
    },
  ];

  return fields;
}

/**
 * Returns visibility/private toggle field
 */
export function getVisibilityFields(
  t: TFunction,
  config: SharedFieldsConfig,
): FieldItem[] {
  const { useDynamicVisibility } = config;

  return [
    {
      name: WidgetTemplateFieldNames.IsPrivate,
      element: CheckBoxSingleBool,
      componentProps: {
        CheckBoxComponent: CheckBoxAsToggleCard,
        options: [{ label: 'checked', value: 'checked' }],
        label: t('home.private'),
        description: t('home.private-widgets-description'),
      },
      ...(useDynamicVisibility && { isVisibleFn: isCreatableKind }),
    },
  ];
}
